import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  FileText as FilePdf, 
  FileText,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Copy,
  Save,
  Move,
  Wand2,
  Upload,
  ScanLine,
  Loader2,
  Image as ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// デフォルトのフィールドマッピング
const DEFAULT_FIELDS = [
  { field_name: "請求年月", source_field: "billing_month", x_position: 130, y_position: 40, font_size: 14, alignment: "center" },
  { field_name: "事業所名", source_field: "facility_name", x_position: 20, y_position: 60, font_size: 10, alignment: "left" },
  { field_name: "利用者名", source_field: "client_name", x_position: 20, y_position: 80, font_size: 12, alignment: "left" },
  { field_name: "受給者番号", source_field: "recipient_number", x_position: 20, y_position: 95, font_size: 10, alignment: "left" },
  { field_name: "サービス種別", source_field: "service_type", x_position: 20, y_position: 120, font_size: 10, alignment: "left" },
  { field_name: "合計単位", source_field: "total_units", x_position: 20, y_position: 140, font_size: 10, alignment: "left" },
  { field_name: "単価", source_field: "unit_price", x_position: 80, y_position: 140, font_size: 10, alignment: "right" },
  { field_name: "請求金額", source_field: "total_amount", x_position: 140, y_position: 140, font_size: 12, alignment: "right" },
  { field_name: "利用者負担額", source_field: "user_burden", x_position: 20, y_position: 160, font_size: 10, alignment: "left" },
  { field_name: "自治体負担額", source_field: "municipality_burden", x_position: 100, y_position: 160, font_size: 10, alignment: "left" },
];

const SOURCE_FIELD_OPTIONS = [
  { value: "billing_month", label: "請求年月" },
  { value: "client_name", label: "利用者名" },
  { value: "recipient_number", label: "受給者番号" },
  { value: "service_type", label: "サービス種別" },
  { value: "total_units", label: "合計単位" },
  { value: "unit_price", label: "単価" },
  { value: "total_amount", label: "請求金額" },
  { value: "user_burden", label: "利用者負担額" },
  { value: "municipality_burden", label: "自治体負担額" },
  { value: "facility_name", label: "事業所名" },
  { value: "facility_address", label: "事業所住所" },
  { value: "facility_phone", label: "事業所電話" },
];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadServiceType, setUploadServiceType] = useState('日中一時支援');
  const [sourceImageUrl, setSourceImageUrl] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    municipality: '津市',
    service_type: '日中一時支援',
    description: '',
    page_size: 'A4',
    orientation: 'portrait',
    fields: [...DEFAULT_FIELDS],
    header_text: '請求書',
    footer_text: '',
    show_grid: false,
    margin_top: 20,
    margin_bottom: 20,
    margin_left: 20,
    margin_right: 20
  });

  const serviceTypes = ['日中一時支援', '移動支援', '地域生活支援'];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/billing-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('テンプレートの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldChange = (index, field, value) => {
    setFormData(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], [field]: value };
      return { ...prev, fields: newFields };
    });
  };

  const addField = () => {
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, {
        field_name: '新規フィールド',
        source_field: 'client_name',
        x_position: 20,
        y_position: 180 + prev.fields.length * 20,
        font_size: 10,
        alignment: 'left'
      }]
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/billing-templates/${editingTemplate.id}`, formData);
      } else {
        await axios.post(`${API}/billing-templates`, formData);
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('保存に失敗しました', error);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      municipality: template.municipality,
      service_type: template.service_type,
      description: template.description,
      page_size: template.page_size,
      orientation: template.orientation,
      fields: template.fields || [],
      header_text: template.header_text,
      footer_text: template.footer_text,
      show_grid: template.show_grid,
      margin_top: template.margin_top,
      margin_bottom: template.margin_bottom,
      margin_left: template.margin_left,
      margin_right: template.margin_right
    });
    setDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    try {
      await axios.delete(`${API}/billing-templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('削除に失敗しました', error);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(`${API}/billing-templates`, {
        ...template,
        name: `${template.name} (コピー)`
      });
      fetchTemplates();
    } catch (error) {
      console.error('複製に失敗しました', error);
    }
  };

  const handlePreview = async (templateId) => {
    try {
      const response = await axios.get(`${API}/billing-templates/${templateId}/preview`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (error) {
      console.error('プレビュー生成に失敗しました', error);
    }
  };

  const handleInitDefault = async () => {
    try {
      await axios.post(`${API}/billing-templates/init-default`);
      fetchTemplates();
    } catch (error) {
      console.error('デフォルトテンプレートの初期化に失敗しました', error);
    }
  };

  const handleUploadFromImage = async () => {
    if (!uploadFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName);
      formData.append('service_type', uploadServiceType);
      formData.append('municipality', '津市');
      
      const response = await axios.post(`${API}/billing-templates/from-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadName('');
      fetchTemplates();
      
      // 成功メッセージ
      alert(`テンプレートを自動生成しました！\n検出フィールド数: ${response.data.detected_fields_count}\n信頼度: ${(response.data.confidence * 100).toFixed(0)}%`);
    } catch (error) {
      console.error('アップロードに失敗しました', error);
      alert('テンプレート生成に失敗しました: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleViewSourceImage = async (templateId) => {
    try {
      const response = await axios.get(`${API}/billing-templates/${templateId}/source-image`);
      if (response.data.source_image) {
        setSourceImageUrl(`data:image/png;base64,${response.data.source_image}`);
      }
    } catch (error) {
      console.error('元画像の取得に失敗しました', error);
      alert('この元画像はありません');
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      municipality: '津市',
      service_type: '日中一時支援',
      description: '',
      page_size: 'A4',
      orientation: 'portrait',
      fields: [...DEFAULT_FIELDS],
      header_text: '請求書',
      footer_text: '',
      show_grid: false,
      margin_top: 20,
      margin_bottom: 20,
      margin_left: 20,
      margin_right: 20
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            PDF帳票テンプレート
          </h1>
          <p className="text-muted-foreground mt-1">
            請求書・書類のPDFテンプレートを管理
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleInitDefault} data-testid="init-default-button">
            <Wand2 size={18} className="mr-2" />
            デフォルト作成
          </Button>
          
          {/* サンプル画像からテンプレート作成 */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-template-button">
                <ScanLine size={18} className="mr-2" />
                サンプルから作成
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>サンプルからテンプレート作成</DialogTitle>
                <DialogDescription>
                  帳票のサンプル画像またはPDFをアップロードすると、AIがフィールド位置を自動検出してテンプレートを作成します
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => document.getElementById('template-file-input').click()}
                  data-testid="upload-dropzone"
                >
                  {uploadFile ? (
                    <div className="space-y-2">
                      {uploadFile.type === 'application/pdf' ? (
                        <FileText size={32} className="mx-auto text-primary" />
                      ) : (
                        <ImageIcon size={32} className="mx-auto text-primary" />
                      )}
                      <p className="text-sm font-medium">{uploadFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(uploadFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        クリックしてファイルを選択<br />
                        （画像またはPDFファイル）
                      </p>
                    </>
                  )}
                  <input
                    id="template-file-input"
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="hidden"
                    data-testid="template-file-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>テンプレート名（任意）</Label>
                  <Input
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="自動生成されます"
                    data-testid="upload-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>サービス種別</Label>
                  <Select value={uploadServiceType} onValueChange={setUploadServiceType}>
                    <SelectTrigger data-testid="upload-service-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setUploadDialogOpen(false);
                    setUploadFile(null);
                    setUploadName('');
                  }}
                >
                  キャンセル
                </Button>
                <Button 
                  type="button"
                  onClick={handleUploadFromImage}
                  disabled={!uploadFile || uploading}
                  data-testid="upload-submit-button"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      解析中...
                    </>
                  ) : (
                    <>
                      <ScanLine size={16} className="mr-2" />
                      テンプレート作成
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-active" data-testid="add-template-button">
                <Plus size={18} className="mr-2" />
                新規テンプレート
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTemplate ? 'テンプレートを編集' : '新規テンプレート作成'}
                </DialogTitle>
                <DialogDescription>
                  請求書PDFのレイアウトをカスタマイズします
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit}>
                <Tabs defaultValue="basic" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="basic">基本設定</TabsTrigger>
                    <TabsTrigger value="fields">フィールド配置</TabsTrigger>
                    <TabsTrigger value="layout">レイアウト</TabsTrigger>
                  </TabsList>

                  {/* 基本設定タブ */}
                  <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>テンプレート名 *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          placeholder="津市標準請求書"
                          required
                          data-testid="template-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>自治体</Label>
                        <Input
                          value={formData.municipality}
                          onChange={(e) => handleChange('municipality', e.target.value)}
                          placeholder="津市"
                          data-testid="template-municipality-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>サービス種別</Label>
                        <Select 
                          value={formData.service_type} 
                          onValueChange={(value) => handleChange('service_type', value)}
                        >
                          <SelectTrigger data-testid="template-service-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceTypes.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>ヘッダーテキスト</Label>
                        <Input
                          value={formData.header_text}
                          onChange={(e) => handleChange('header_text', e.target.value)}
                          placeholder="請求書"
                          data-testid="template-header-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>説明</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        placeholder="テンプレートの説明"
                        rows={2}
                        data-testid="template-description-input"
                      />
                    </div>
                  </TabsContent>

                  {/* フィールド配置タブ */}
                  <TabsContent value="fields" className="space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">
                        フィールドの位置（mm単位）と表示設定を編集
                      </p>
                      <Button type="button" variant="outline" size="sm" onClick={addField}>
                        <Plus size={14} className="mr-1" />
                        フィールド追加
                      </Button>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-secondary/50">
                            <TableHead className="w-[120px]">表示名</TableHead>
                            <TableHead className="w-[140px]">データソース</TableHead>
                            <TableHead className="w-[70px]">X (mm)</TableHead>
                            <TableHead className="w-[70px]">Y (mm)</TableHead>
                            <TableHead className="w-[70px]">サイズ</TableHead>
                            <TableHead className="w-[90px]">配置</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.fields.map((field, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Input
                                  value={field.field_name}
                                  onChange={(e) => handleFieldChange(index, 'field_name', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Select 
                                  value={field.source_field} 
                                  onValueChange={(value) => handleFieldChange(index, 'source_field', value)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SOURCE_FIELD_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={field.x_position}
                                  onChange={(e) => handleFieldChange(index, 'x_position', parseFloat(e.target.value))}
                                  className="h-8 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={field.y_position}
                                  onChange={(e) => handleFieldChange(index, 'y_position', parseFloat(e.target.value))}
                                  className="h-8 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={field.font_size}
                                  onChange={(e) => handleFieldChange(index, 'font_size', parseInt(e.target.value))}
                                  className="h-8 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Select 
                                  value={field.alignment} 
                                  onValueChange={(value) => handleFieldChange(index, 'alignment', value)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="left">左</SelectItem>
                                    <SelectItem value="center">中央</SelectItem>
                                    <SelectItem value="right">右</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => removeField(index)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>

                  {/* レイアウトタブ */}
                  <TabsContent value="layout" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>用紙サイズ</Label>
                        <Select 
                          value={formData.page_size} 
                          onValueChange={(value) => handleChange('page_size', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A4">A4</SelectItem>
                            <SelectItem value="B5">B5</SelectItem>
                            <SelectItem value="A5">A5</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>向き</Label>
                        <Select 
                          value={formData.orientation} 
                          onValueChange={(value) => handleChange('orientation', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="portrait">縦</SelectItem>
                            <SelectItem value="landscape">横</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>上余白 (mm)</Label>
                        <Input
                          type="number"
                          value={formData.margin_top}
                          onChange={(e) => handleChange('margin_top', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>下余白 (mm)</Label>
                        <Input
                          type="number"
                          value={formData.margin_bottom}
                          onChange={(e) => handleChange('margin_bottom', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>左余白 (mm)</Label>
                        <Input
                          type="number"
                          value={formData.margin_left}
                          onChange={(e) => handleChange('margin_left', parseFloat(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>右余白 (mm)</Label>
                        <Input
                          type="number"
                          value={formData.margin_right}
                          onChange={(e) => handleChange('margin_right', parseFloat(e.target.value))}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.show_grid}
                        onCheckedChange={(checked) => handleChange('show_grid', checked)}
                      />
                      <Label>グリッド線を表示（デバッグ用）</Label>
                    </div>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    キャンセル
                  </Button>
                  <Button type="submit" data-testid="template-submit-button">
                    <Save size={16} className="mr-2" />
                    {editingTemplate ? '更新' : '作成'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* テンプレート一覧 */}
      {templates.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center">
            <FileText size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">テンプレートがありません</p>
            <p className="text-sm text-muted-foreground mt-1">
              「デフォルト作成」でデフォルトテンプレートを作成できます
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="card-surface" data-testid={`template-card-${template.id}`}>
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText size={20} className="text-primary" />
                    {template.name}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {template.municipality} | {template.service_type}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {template.description && (
                  <p className="text-sm text-muted-foreground">{template.description}</p>
                )}
                
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="badge-neutral">{template.page_size}</span>
                  <span className="badge-neutral">
                    {template.orientation === 'portrait' ? '縦' : '横'}
                  </span>
                  <span className="badge-neutral">
                    {template.fields?.length || 0}フィールド
                  </span>
                </div>
                
                <div className="flex gap-2 pt-2 border-t border-border flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handlePreview(template.id)}
                    data-testid={`preview-template-${template.id}`}
                  >
                    <Eye size={14} className="mr-1" />
                    プレビュー
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleViewSourceImage(template.id)}
                    data-testid={`source-image-${template.id}`}
                  >
                    <ImageIcon size={14} className="mr-1" />
                    元画像
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEdit(template)}
                    data-testid={`edit-template-${template.id}`}
                  >
                    <Pencil size={14} className="mr-1" />
                    編集
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDuplicate(template)}
                    data-testid={`duplicate-template-${template.id}`}
                  >
                    <Copy size={14} />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>テンプレートを削除しますか？</AlertDialogTitle>
                        <AlertDialogDescription>
                          この操作は取り消せません。
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>キャンセル</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(template.id)}>
                          削除する
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* プレビューモーダル */}
      {previewUrl && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setPreviewUrl(null);
            window.URL.revokeObjectURL(previewUrl);
          }}
        >
          <Card 
            className="card-surface w-full max-w-4xl h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="flex flex-row items-center justify-between py-3 relative z-20">
              <CardTitle>プレビュー</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                className="relative z-30"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreviewUrl(null);
                  window.URL.revokeObjectURL(previewUrl);
                }}
                data-testid="preview-close-button"
              >
                閉じる
              </Button>
            </CardHeader>
            <CardContent className="p-0 h-[calc(100%-60px)]">
              <iframe 
                src={previewUrl} 
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* 元画像表示モーダル */}
      {sourceImageUrl && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSourceImageUrl(null)}
        >
          <Card 
            className="card-surface w-full max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle>元画像（サンプル）</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSourceImageUrl(null)}
                data-testid="source-image-close-button"
              >
                閉じる
              </Button>
            </CardHeader>
            <CardContent>
              <img 
                src={sourceImageUrl} 
                alt="元画像" 
                className="w-full rounded-lg border border-border"
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
